import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import { NodeSelection } from "@tiptap/pm/state";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import Placeholder from "@tiptap/extension-placeholder";
import { toast } from "react-toastify";
import FontSize, { FONT_SIZE_OPTIONS } from "../extensions/tiptapFontSize";
import CustomImage from "../extensions/tiptapCustomImage";
import { insertImageSmart } from "../utils/ribbonInsertImage";
import ImageCropModal from "./ImageCropModal";
import ImageResizeOverlay from "./ImageResizeOverlay";
import { resizeImage } from "../utils/imageResize";

const rawApiUrl = process.env.REACT_APP_API_URL || "https://api.isyllabi.com/api";
const API_URL = rawApiUrl.endsWith("/api") ? rawApiUrl : rawApiUrl.replace(/\/?$/, "") + "/api";

const icons = {
  text: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16M4 12h10M4 18h7" />
    </svg>
  ),
  paragraph: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
    </svg>
  ),
  media: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.75}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  ),
  table: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.75}
        d="M3 10h18M3 14h18m-9-4v8m-7 0h7a1 1 0 001-1v-4a1 1 0 00-1-1H3a1 1 0 00-1 1v4a1 1 0 001 1zm10 0h7a1 1 0 001-1v-4a1 1 0 00-1-1h-7a1 1 0 00-1 1v4a1 1 0 001 1z"
      />
    </svg>
  ),
  layout: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.75}
        d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
      />
    </svg>
  ),
};

const CATEGORIES = [
  { id: "text", label: "Text", icon: icons.text },
  { id: "paragraph", label: "Paragraph", icon: icons.paragraph },
  { id: "media", label: "Media", icon: icons.media },
  { id: "table", label: "Table", icon: icons.table },
  { id: "layout", label: "Layout", icon: icons.layout },
];

/** Upload image file; returns public image URL (async). */
async function uploadImageToServerFile(file) {
  const formData = new FormData();
  formData.append("image", file);
  toast.info("Uploading image...");

  const token = localStorage.getItem("token");
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_URL}/upload/image`, {
    method: "POST",
    headers,
    body: formData,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const result = await res.json();
  if (!result.success) throw new Error(result.error || "Upload failed");
  let imageUrl = result.url;
  if (imageUrl && !/^https?:\/\//.test(imageUrl)) {
    imageUrl = `${window.location.protocol}//${window.location.hostname}:5001${imageUrl}`;
  }
  toast.success("Image uploaded");
  return imageUrl;
}

/**
 * Ribbon-style Tiptap editor for course content (class description & notes).
 * Props mirror the old Quill wrapper: value (HTML), onChange(html).
 */
const RibbonTiptapEditor = ({
  value,
  onChange,
  placeholder = "Start typing…",
  minHeight = "220px",
  className = "",
}) => {
  const [activeCat, setActiveCat] = useState("text");
  /** Bumps when selection moves so font-size dropdown reflects active marks */
  const [selectionEpoch, setSelectionEpoch] = useState(0);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState("");
  const fileInputRef = useRef(null);
  const extensions = useMemo(
    () => [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextStyle,
      FontSize,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: "text-blue-600 underline" },
      }),
      CustomImage.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: { class: "rounded-lg shadow max-w-full h-auto my-2" },
      }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder }),
    ],
    [placeholder]
  );

  const editor = useEditor({
    extensions,
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "focus:outline-none prose prose-sm max-w-none text-gray-800 px-3 py-2 sm:px-4 sm:py-3",
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    const v = value ?? "";
    const cur = editor.getHTML();
    if (v === cur) return;
    // Avoid clobbering editor when parent state is briefly behind (e.g. multiple image uploads).
    if (editor.isFocused) return;
    editor.commands.setContent(v, false);
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    const onSel = () => setSelectionEpoch((n) => n + 1);
    editor.on("selectionUpdate", onSel);
    return () => editor.off("selectionUpdate", onSel);
  }, [editor]);

  const chain = useCallback(() => editor?.chain().focus(), [editor]);

  const insertImageFromFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !editor) return;
    for (const file of files) {
      if (!file.type.startsWith("image/")) continue;
      try {
        const compressed = await resizeImage(file, 1600, 1200, 0.82);
        const url = await uploadImageToServerFile(compressed);
        insertImageSmart(editor, { src: url });
      } catch (err) {
        console.error(err);
        toast.error(err.message || "Could not upload image");
      }
    }
  };

  const insertImageBase64 = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (!files.length || !editor) return;
      for (const file of files) {
        if (!file.type.startsWith("image/")) continue;
        const data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        insertImageSmart(editor, { src: data });
      }
      toast.success(files.length > 1 ? "Images inserted" : "Image inserted (embedded)");
    };
    input.click();
  };

  const insertImageUrl = () => {
    const url = window.prompt("Image URL (https://…)", "https://");
    if (url && /^https?:\/\//i.test(url.trim())) {
      insertImageSmart(editor, { src: url.trim() });
    } else if (url) toast.error("Enter a valid http(s) URL");
  };

  const openCropModal = () => {
    if (!editor) return;
    const sel = editor.state.selection;
    if (!(sel instanceof NodeSelection) || sel.node.type.name !== "image") {
      toast.info("Click an image first, then use Crop.");
      return;
    }
    const src = sel.node.attrs.src;
    if (!src) return;
    setCropSrc(src);
    setCropOpen(true);
  };

  const onCropBlob = async (blob) => {
    if (!editor) return;
    try {
      const file = new File([blob], "crop.jpg", { type: "image/jpeg" });
      const url = await uploadImageToServerFile(file);
      editor.chain().focus().updateAttributes("image", { src: url, width: null }).run();
      toast.success("Image updated");
    } catch (err) {
      toast.error(err.message || "Crop upload failed");
    }
  };

  const setLink = () => {
    const prev = editor?.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") {
      chain()?.extendMarkRange("link").unsetLink().run();
      return;
    }
    chain()?.extendMarkRange("link").setLink({ href: url }).run();
  };

  const ToolBtn = ({ onClick, children, disabled, title }) => (
    <button
      type="button"
      title={title}
      disabled={disabled || !editor}
      onClick={onClick}
      className="px-2 py-1 text-xs font-medium rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-cyan-50 hover:border-cyan-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      {children}
    </button>
  );

  const renderSecondRow = () => {
    if (!editor) return null;
    const e = editor;
    const c = () => e.chain().focus();

    switch (activeCat) {
      case "text": {
        const currentSize = e.getAttributes("textStyle")?.fontSize ?? "";
        const presetValues = FONT_SIZE_OPTIONS.map((o) => o.value).filter(Boolean);
        const isOrphanSize = Boolean(currentSize && !presetValues.includes(currentSize));
        const sizeOptions = isOrphanSize
          ? [
              FONT_SIZE_OPTIONS[0],
              { label: `${currentSize} (custom)`, value: currentSize },
              ...FONT_SIZE_OPTIONS.slice(1),
            ]
          : FONT_SIZE_OPTIONS;
        return (
          <>
            <label
              className="inline-flex items-center gap-1.5 pl-1 pr-2 py-1 text-xs font-medium rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-cyan-50 hover:border-cyan-200"
              data-selection-epoch={selectionEpoch}
            >
              <span className="text-gray-500 whitespace-nowrap hidden sm:inline">Size</span>
              <select
                aria-label="Font size"
                className="max-w-[6rem] sm:max-w-[7.5rem] text-xs bg-transparent border-0 py-0.5 pr-6 pl-0 focus:ring-0 cursor-pointer text-gray-800 font-medium"
                value={currentSize}
                onChange={(ev) => {
                  const v = ev.target.value;
                  if (!v) {
                    c().unsetFontSize().run();
                  } else {
                    c().setFontSize(v).run();
                  }
                  setSelectionEpoch((n) => n + 1);
                }}
              >
                {sizeOptions.map((opt) => (
                  <option key={(opt.value || "default") + opt.label} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <ToolBtn onClick={() => c().toggleBold().run()} disabled={!e.can().toggleBold()}>
              Bold
            </ToolBtn>
            <ToolBtn onClick={() => c().toggleItalic().run()} disabled={!e.can().toggleItalic()}>
              Italic
            </ToolBtn>
            <ToolBtn onClick={() => c().toggleUnderline().run()} disabled={!e.can().toggleUnderline()}>
              Underline
            </ToolBtn>
            <ToolBtn onClick={() => c().toggleStrike().run()} disabled={!e.can().toggleStrike()}>
              Strike
            </ToolBtn>
            <ToolBtn onClick={() => c().toggleCode().run()} disabled={!e.can().toggleCode()}>
              Code
            </ToolBtn>
            <ToolBtn onClick={() => c().unsetAllMarks().clearNodes().run()}>Clear</ToolBtn>
            <label className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-gray-200 bg-white cursor-pointer hover:bg-cyan-50">
              Color
              <input
                type="color"
                defaultValue="#111827"
                className="w-7 h-6 rounded border-0 cursor-pointer p-0"
                onInput={(ev) => c().setColor(ev.target.value).run()}
              />
            </label>
            <ToolBtn onClick={() => c().toggleHighlight({ color: "#fef08a" }).run()}>Highlight</ToolBtn>
          </>
        );
      }
      case "paragraph":
        return (
          <>
            <ToolBtn onClick={() => c().toggleHeading({ level: 1 }).run()}>H1</ToolBtn>
            <ToolBtn onClick={() => c().toggleHeading({ level: 2 }).run()}>H2</ToolBtn>
            <ToolBtn onClick={() => c().toggleHeading({ level: 3 }).run()}>H3</ToolBtn>
            <ToolBtn onClick={() => c().setParagraph().run()}>P</ToolBtn>
            <ToolBtn onClick={() => c().toggleBulletList().run()}>• List</ToolBtn>
            <ToolBtn onClick={() => c().toggleOrderedList().run()}>1. List</ToolBtn>
            <ToolBtn onClick={() => c().toggleBlockquote().run()}>Quote</ToolBtn>
            <ToolBtn onClick={() => c().toggleCodeBlock().run()}>Code ▢</ToolBtn>
            <ToolBtn onClick={() => c().setHorizontalRule().run()}>HR</ToolBtn>
            <span className="w-px h-5 bg-gray-300 mx-1 hidden sm:inline-block" />
            <ToolBtn onClick={() => c().setTextAlign("left").run()}>◧</ToolBtn>
            <ToolBtn onClick={() => c().setTextAlign("center").run()}>◎</ToolBtn>
            <ToolBtn onClick={() => c().setTextAlign("right").run()}>◨</ToolBtn>
            <ToolBtn onClick={() => c().setTextAlign("justify").run()}>≡</ToolBtn>
          </>
        );
      case "media": {
        const sel = e.state.selection;
        const imageSelected =
          sel instanceof NodeSelection && sel.node.type.name === "image";
        const imgAttrs = imageSelected ? e.getAttributes("image") : {};
        return (
          <>
            <ToolBtn onClick={() => fileInputRef.current?.click()} title="Upload one or more images to server">
              Image ↑ server
            </ToolBtn>
            <ToolBtn onClick={insertImageBase64} title="Embed one or more images as base64">
              Image base64
            </ToolBtn>
            <ToolBtn onClick={insertImageUrl}>Image URL</ToolBtn>
            <ToolBtn onClick={setLink}>Link</ToolBtn>
            <ToolBtn onClick={() => c().unsetLink().run()}>Unlink</ToolBtn>
            {imageSelected && (
              <span
                className="flex flex-wrap gap-1 items-center border-l border-gray-300 pl-2 ml-1"
                data-selection-epoch={selectionEpoch}
              >
                <span className="text-[10px] text-gray-500 uppercase tracking-wide mr-0.5">Img</span>
                {["200px", "400px", "600px", "100%"].map((w) => (
                  <ToolBtn
                    key={w}
                    title={`Width ${w}`}
                    onClick={() => e.chain().focus().updateAttributes("image", { width: w }).run()}
                  >
                    {w}
                  </ToolBtn>
                ))}
                <ToolBtn
                  title="Clear custom width"
                  onClick={() => e.chain().focus().updateAttributes("image", { width: null }).run()}
                >
                  Reset W
                </ToolBtn>
                <ToolBtn onClick={openCropModal} title="Crop selected image">
                  Crop
                </ToolBtn>
                {imgAttrs?.width && (
                  <span className="text-[10px] text-gray-500 max-w-[4rem] truncate" title={imgAttrs.width}>
                    ({imgAttrs.width})
                  </span>
                )}
              </span>
            )}
          </>
        );
      }
      case "table":
        return (
          <>
            <ToolBtn onClick={() => c().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
              + Table 3×3
            </ToolBtn>
            <ToolBtn onClick={() => c().addRowAfter().run()}>+ Row</ToolBtn>
            <ToolBtn onClick={() => c().addColumnAfter().run()}>+ Col</ToolBtn>
            <ToolBtn onClick={() => c().deleteRow().run()}>− Row</ToolBtn>
            <ToolBtn onClick={() => c().deleteColumn().run()}>− Col</ToolBtn>
            <ToolBtn onClick={() => c().deleteTable().run()}>Delete table</ToolBtn>
            <ToolBtn onClick={() => c().mergeCells().run()}>Merge</ToolBtn>
            <ToolBtn onClick={() => c().splitCell().run()}>Split</ToolBtn>
          </>
        );
      case "layout":
        return (
          <>
            <ToolBtn onClick={() => c().undo().run()}>Undo</ToolBtn>
            <ToolBtn onClick={() => c().redo().run()}>Redo</ToolBtn>
            <ToolBtn onClick={() => c().setHardBreak().run()}>Line break</ToolBtn>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`ribbon-tiptap-scope rounded-lg overflow-hidden border border-gray-200 bg-white ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={insertImageFromFile}
      />

      {editor ? <ImageResizeOverlay editor={editor} tick={selectionEpoch} /> : null}

      <div className="bg-gradient-to-b from-slate-50 to-slate-100/90 border-b border-gray-200 px-1 py-1.5 sm:px-2">
        <div className="flex flex-wrap justify-center sm:justify-start gap-1">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActiveCat(cat.id)}
              className={`flex flex-col items-center justify-center min-w-[4.25rem] sm:min-w-[5rem] px-1.5 py-1.5 rounded-lg border transition-all ${
                activeCat === cat.id
                  ? "bg-white border-cyan-400 shadow-sm text-gray-900 ring-1 ring-cyan-200"
                  : "border-transparent text-gray-600 hover:bg-white/80 hover:border-gray-200"
              }`}
            >
              <span className="text-cyan-600">{cat.icon}</span>
              <span className="text-[10px] sm:text-xs font-semibold mt-0.5">{cat.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-gray-50/95 border-b border-gray-200 px-2 py-1.5">
        <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">
          {CATEGORIES.find((x) => x.id === activeCat)?.label} tools
        </p>
        <div className="flex flex-wrap gap-1 items-center">{renderSecondRow()}</div>
      </div>

      <div
        className="relative bg-white border-t border-gray-100 overflow-y-auto"
        style={{ minHeight }}
      >
        {editor ? (
          <EditorContent editor={editor} />
        ) : (
          <div className="px-4 py-8 text-center text-sm text-gray-400">Loading editor…</div>
        )}
        <ImageCropModal
          inline
          open={cropOpen}
          imageSrc={cropSrc}
          onClose={() => {
            setCropOpen(false);
            setCropSrc("");
          }}
          onApplyBlob={onCropBlob}
        />
      </div>

      <style>{`
        .ribbon-tiptap-scope .ProseMirror {
          outline: none;
          min-height: inherit;
        }
        .ribbon-tiptap-scope .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #9ca3af;
          pointer-events: none;
          height: 0;
        }
        .ribbon-tiptap-scope table {
          border-collapse: collapse;
          width: 100%;
          margin: 0.5rem 0;
          border: 1px solid #e5e7eb;
          border-radius: 0.375rem;
          overflow: hidden;
        }
        .ribbon-tiptap-scope th, .ribbon-tiptap-scope td {
          border: 1px solid #e5e7eb;
          padding: 0.35rem 0.5rem;
          vertical-align: top;
        }
        .ribbon-tiptap-scope th {
          background: #f9fafb;
          font-weight: 600;
        }
        .ribbon-tiptap-scope img {
          max-width: 100%;
          height: auto;
        }
      `}</style>
    </div>
  );
};

export default RibbonTiptapEditor;
